import { useState, useEffect, useCallback } from 'react'
import {
  PageHeader,
  StatusBadge,
  DataTable,
  ConfirmDialog,
  ColumnSettingsButton,
  colors,
  btnPrimary,
  btnDanger,
  btnSecondary,
} from '../../components/ui'
import type { Column } from '../../components/ui'
import { getAuthToken } from '../../services/api'
import { useTableColumns } from '../../hooks/use-table-columns'

interface AnalyticsStatus {
  readonly enabled: boolean
  readonly lastUpload: string | null
  readonly rowsLast24h: number
  readonly batchesLast24h: number
}

interface UploadRow {
  readonly id: string
  readonly organizationId: string
  readonly batchId: string
  readonly entityKind: string
  readonly rowCount: number
  readonly bytesSent: number
  readonly schemaVersion: number
  readonly uploadedAt: string
  readonly ackAt: string | null
  readonly ackError: string | null
}

interface UploadListResponse {
  readonly success: boolean
  readonly data: UploadRow[]
  readonly meta: { total: number; page: number; limit: number }
}

function formatDateTime(iso: string | null): string {
  if (!iso) return 'Never'
  try {
    return new Date(iso).toLocaleString()
  } catch {
    return iso
  }
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`
}

const UPLOAD_COLUMNS: readonly Column<UploadRow>[] = [
  {
    key: 'uploadedAt',
    header: 'Time',
    sortable: true,
    render: (row) => formatDateTime(row.uploadedAt),
  },
  {
    key: 'entityKind',
    header: 'Entity',
    sortable: true,
    filterable: true,
    render: (row) => row.entityKind,
  },
  {
    key: 'rowCount',
    header: 'Rows',
    sortable: true,
    render: (row) => row.rowCount.toLocaleString(),
  },
  {
    key: 'bytesSent',
    header: 'Bytes',
    sortable: true,
    render: (row) => formatBytes(row.bytesSent),
  },
  {
    key: 'status',
    header: 'Status',
    render: (row) => {
      if (row.ackError) return <StatusBadge status="error" label="Failed" />
      if (row.ackAt) return <StatusBadge status="active" label="Acked" />
      return <StatusBadge status="inactive" label="Pending" />
    },
  },
]

export function AnalyticsSharingPage() {
  const [status, setStatus] = useState<AnalyticsStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { tableId: _, ...tableColumnProps } = useTableColumns('analytics-sharing')

  // Upload history state
  const [uploads, setUploads] = useState<UploadRow[]>([])
  const [uploadMeta, setUploadMeta] = useState<{ total: number; page: number; limit: number } | null>(null)
  const [uploadPage, setUploadPage] = useState(1)
  const [uploadsLoading, setUploadsLoading] = useState(false)

  // Deletion dialog state
  const [deletionDialogOpen, setDeletionDialogOpen] = useState(false)
  const [deletionLoading, setDeletionLoading] = useState(false)
  const [deletionError, setDeletionError] = useState<string | null>(null)

  const fetchStatus = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await fetch('/api/admin/analytics/status', {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      })
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string }
        throw new Error(body.error ?? `Request failed (${res.status})`)
      }
      const body = (await res.json()) as { data: AnalyticsStatus }
      setStatus(body.data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load analytics status')
    } finally {
      setLoading(false)
    }
  }, [])

  const fetchUploads = useCallback(async (page: number) => {
    setUploadsLoading(true)
    try {
      const params = new URLSearchParams({ page: String(page), limit: '50' })
      const res = await fetch(`/api/admin/analytics/uploads?${params.toString()}`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      })
      if (!res.ok) return
      const body = (await res.json()) as UploadListResponse
      if (body.success) {
        setUploads(body.data)
        setUploadMeta(body.meta)
      }
    } catch {
      // Best-effort — don't block the main status view
    } finally {
      setUploadsLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchStatus()
  }, [fetchStatus])

  useEffect(() => {
    fetchUploads(uploadPage)
  }, [fetchUploads, uploadPage])

  const handleToggle = useCallback(async () => {
    if (!status) return
    setSaving(true)
    setError(null)
    const newEnabled = !status.enabled
    try {
      const res = await fetch('/api/admin/analytics/sharing', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAuthToken()}`,
        },
        body: JSON.stringify({ enabled: newEnabled }),
      })
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string }
        throw new Error(body.error ?? `Request failed (${res.status})`)
      }
      setStatus((prev) => (prev ? { ...prev, enabled: newEnabled } : prev))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update sharing setting')
    } finally {
      setSaving(false)
    }
  }, [status])

  const handleExportCsv = useCallback(() => {
    const a = document.createElement('a')
    a.href = `/api/admin/analytics/uploads/export?format=csv`
    a.setAttribute('data-auth', 'true')
    // Trigger download via fetch to include auth header
    void fetch('/api/admin/analytics/uploads/export?format=csv', {
      headers: { Authorization: `Bearer ${getAuthToken()}` },
    }).then(async (res) => {
      if (!res.ok) return
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = 'analytics-uploads.csv'
      link.click()
      URL.revokeObjectURL(url)
    })
  }, [])

  const handleDeletionConfirm = useCallback(async () => {
    setDeletionLoading(true)
    setDeletionError(null)
    try {
      const res = await fetch('/api/admin/analytics/deletion-request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAuthToken()}`,
        },
        body: JSON.stringify({}),
      })
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string }
        throw new Error(body.error ?? `Request failed (${res.status})`)
      }
      setDeletionDialogOpen(false)
      await fetchStatus()
      await fetchUploads(1)
      setUploadPage(1)
    } catch (err) {
      setDeletionError(err instanceof Error ? err.message : 'Failed to submit deletion request')
    } finally {
      setDeletionLoading(false)
    }
  }, [fetchStatus, fetchUploads])

  // Summary derived from upload meta
  const totalBatches = uploadMeta?.total ?? 0
  const totalRows = uploads.reduce((sum, r) => sum + r.rowCount, 0)
  const earliestUpload = uploads.length > 0 ? (uploads[uploads.length - 1]?.uploadedAt ?? null) : null

  return (
    <div style={{ padding: '24px', maxWidth: '900px' }}>
      <PageHeader
        title="Analytics Sharing"
        actions={<ColumnSettingsButton columns={UPLOAD_COLUMNS} {...tableColumnProps} />}
      />

      {/* Sharing toggle card */}
      <div
        style={{
          background: colors.bgCard,
          border: `1px solid ${colors.border}`,
          borderRadius: '6px',
          padding: '20px 24px',
          marginBottom: '24px',
        }}
      >
        <p style={{ color: colors.text, marginTop: 0, marginBottom: '16px', lineHeight: '1.6' }}>
          When enabled, this deployment sends de-identified operational data to MedFlow for cross-organization
          benchmarking and clinical meta-analysis. All patient identifiers are replaced with one-way HMAC hashes before
          transmission. You can opt out at any time.
        </p>

        {error && (
          <div
            style={{
              background: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '4px',
              color: '#dc2626',
              padding: '10px 14px',
              marginBottom: '16px',
              fontSize: '0.875rem',
            }}
          >
            {error}
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <span style={{ color: colors.textMuted, fontSize: '0.875rem' }}>Sharing status:</span>
          {loading ? (
            <span style={{ color: colors.textMuted, fontSize: '0.875rem' }}>Loading…</span>
          ) : (
            <StatusBadge
              status={status?.enabled ? 'active' : 'inactive'}
              label={status?.enabled ? 'Enabled' : 'Disabled'}
            />
          )}
          <button onClick={handleToggle} disabled={loading || saving || status === null} style={btnPrimary}>
            {saving ? 'Saving…' : status?.enabled ? 'Disable Sharing' : 'Enable Sharing'}
          </button>
        </div>
      </div>

      {status && (
        <div
          style={{
            background: colors.bgCard,
            border: `1px solid ${colors.border}`,
            borderRadius: '6px',
            padding: '20px 24px',
            marginBottom: '24px',
          }}
        >
          <h3 style={{ margin: '0 0 16px', fontSize: '0.9375rem', color: colors.text }}>
            Upload Summary (Last 24 Hours)
          </h3>
          <dl
            style={{
              display: 'grid',
              gridTemplateColumns: 'auto 1fr',
              gap: '8px 24px',
              margin: 0,
              fontSize: '0.875rem',
            }}
          >
            <dt style={{ color: colors.textMuted, fontWeight: 600 }}>Rows uploaded</dt>
            <dd style={{ color: colors.text, margin: 0 }}>{status.rowsLast24h.toLocaleString()}</dd>
            <dt style={{ color: colors.textMuted, fontWeight: 600 }}>Batches sent</dt>
            <dd style={{ color: colors.text, margin: 0 }}>{status.batchesLast24h.toLocaleString()}</dd>
            <dt style={{ color: colors.textMuted, fontWeight: 600 }}>Last upload</dt>
            <dd style={{ color: colors.text, margin: 0 }}>{formatDateTime(status.lastUpload)}</dd>
          </dl>
        </div>
      )}

      {/* Upload history table */}
      <div
        style={{
          background: colors.bgCard,
          border: `1px solid ${colors.border}`,
          borderRadius: '6px',
          padding: '20px 24px',
          marginBottom: '24px',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <div>
            <h3 style={{ margin: '0 0 4px', fontSize: '0.9375rem', color: colors.text }}>Upload History</h3>
            {uploadMeta && (
              <p style={{ margin: 0, fontSize: '0.8125rem', color: colors.textMuted }}>
                Total retained: {totalRows.toLocaleString()} rows across {totalBatches.toLocaleString()} batches
                {earliestUpload ? ` since ${formatDateTime(earliestUpload)}` : ''}
              </p>
            )}
          </div>
          <button onClick={handleExportCsv} style={btnSecondary} disabled={uploadsLoading || uploads.length === 0}>
            Export history (CSV)
          </button>
        </div>

        {uploadsLoading ? (
          <p style={{ color: colors.textMuted, fontSize: '0.875rem' }}>Loading…</p>
        ) : (
          <DataTable<UploadRow>
            columns={UPLOAD_COLUMNS}
            rows={uploads}
            keyField="id"
            emptyMessage="No upload history yet"
            {...tableColumnProps}
          />
        )}

        {uploadMeta && uploadMeta.total > uploadMeta.limit && (
          <div style={{ display: 'flex', gap: '8px', marginTop: '16px', justifyContent: 'flex-end' }}>
            <button
              style={btnSecondary}
              disabled={uploadPage <= 1}
              onClick={() => setUploadPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </button>
            <span style={{ color: colors.textMuted, fontSize: '0.875rem', alignSelf: 'center' }}>
              Page {uploadPage} of {Math.ceil(uploadMeta.total / uploadMeta.limit)}
            </span>
            <button
              style={btnSecondary}
              disabled={uploadPage >= Math.ceil(uploadMeta.total / uploadMeta.limit)}
              onClick={() => setUploadPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Data deletion section */}
      <div
        style={{
          background: colors.bgCard,
          border: `1px solid ${colors.border}`,
          borderRadius: '6px',
          padding: '20px 24px',
        }}
      >
        <h3 style={{ margin: '0 0 8px', fontSize: '0.9375rem', color: colors.text }}>Data Deletion</h3>
        <p style={{ color: colors.textMuted, fontSize: '0.875rem', marginTop: 0, marginBottom: '16px' }}>
          Request permanent deletion of all analytics data MedFlow has ingested from this installation. This cannot be
          undone. SLA: 30 days.
        </p>
        {deletionError && (
          <div
            style={{
              background: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '4px',
              color: '#dc2626',
              padding: '10px 14px',
              marginBottom: '12px',
              fontSize: '0.875rem',
            }}
          >
            {deletionError}
          </div>
        )}
        <button style={btnDanger} onClick={() => setDeletionDialogOpen(true)}>
          Request data deletion
        </button>
      </div>

      <ConfirmDialog
        isOpen={deletionDialogOpen}
        onClose={() => {
          setDeletionDialogOpen(false)
          setDeletionError(null)
        }}
        onConfirm={() => void handleDeletionConfirm()}
        title="Request Analytics Data Deletion"
        message="This permanently deletes all analytics data MedFlow has ingested from this installation. SLA: 30 days from request. This action cannot be undone."
        confirmLabel="Request Deletion"
        cancelLabel="Cancel"
        variant="danger"
        loading={deletionLoading}
      />
    </div>
  )
}
